import "./style.css";
import { ProfileSkeleton } from "./ProfileSkeleton";
import { ProfileView } from "./ProfileView";
import { useProfileContent } from "./useProfileContent";
import "./styleGuide.css";
import { useLocation } from "react-router-dom";
import React, { useEffect, useState, createContext } from "react";
import apiInstance, { API_SERVER_ENDPOINT } from "../../shared/utils/axios";
import { updatePageMeta } from "../../shared/utils/meta";

export const userContext = createContext<DataProps | null>(null);

// 유저 정보
export interface UserDataProps {
  idx?: number;
  staticUserIdx?: number;
  is_verified?: boolean;
  nickname: string;
  sub_nickname?: string; // Full Name (활동명)
  description?: string; // 소개
  handle: string; // handle
  profile_picture_update_ts?: number; // Profile Update Ts
  deep_link?: string; // Deep Link
}

// 링크 정보
export interface LinkDataProps {
  idx: number;
  title?: string; // 링크 이름
  l_url: string; // 링크
  l_type?: "OTHER" | "IG" | "YT" | "FB" | "TIKTOK" | "IMDB" | "IBDB"; // 링크 타입
}

// 영상 정보
export interface VideoDataProps {
  idx: number;
  title: string; // 영상 제목
  v_type?: "UPLOAD" | "YT" | undefined; // 영상 타입
  v_url?: string; // 외부 링크
}

export interface CreatorRoleDataProps {
  idx: number;
  displayOrder?: number;
  isDelete?: boolean;
  roleNameEn?: string | null;
  roleNameKr?: string | null;
}

export interface CreatorQueryResponseProps {
  creatorIdx?: number;
  creatorName?: string;
  performanceIdx?: number;
  profilePictureIdx?: number;
  profilePictureUpdateTime?: number;
  roles?: CreatorRoleDataProps[];
}

// 퍼포먼스(필모그래피) 정보
export interface PerformanceDataProps {
  idx: number;
  userIdx?: number;
  performanceIdx: number;
  performanceTitleEn: string; // 공연 제목
  performanceTitleLocal?: string; // 공연 제목
  status: string; // 상태 (ENDED 등)
  startAtUtc?: string; // 시작일
  endAtUtc?: string;
  startTime?: string;
  endTime?: string;
  performanceRegTs?: string;
  watchedRegTs?: string;
  timezone?: string;
  startPrecision?: string;
  endPrecision?: string;
  isOpenRun?: boolean;
  posterImageType?: string;
  sourceType?: string;
  creatorQueryResponse?: CreatorQueryResponseProps;
  fileResponse?: {
    idx: number;
    fileKind: string;
    savedName?: string;
  };
}

export interface PerformanceArchiveProps {
  accountType?: string;
  filmographyList: PerformanceDataProps[];
  watchedPerformanceList: PerformanceDataProps[];
}

export interface DataProps {
  user: UserDataProps;
  link: LinkDataProps[];
  video: VideoDataProps[];
  performance: PerformanceArchiveProps;
}

const createEmptyPerformanceArchive = (): PerformanceArchiveProps => ({
  filmographyList: [],
  watchedPerformanceList: [],
});

export const hasPerformanceContents = (performance?: PerformanceArchiveProps) =>
  Boolean(
    performance &&
      (performance.filmographyList.length > 0 ||
        performance.watchedPerformanceList.length > 0),
  );

const normalizePerformanceArchive = (
  value: PerformanceArchiveProps,
): PerformanceArchiveProps => {
  return {
    accountType: value.accountType,
    filmographyList: value.filmographyList || [],
    watchedPerformanceList: value.watchedPerformanceList || [],
  };
};

const ProfilePage = () => {
  const location = useLocation();
  const handle = location.pathname.split("/").filter(Boolean).pop();

  const { content, requests, userIdx } = useProfileContent(handle || "");

  // 유저 정보, 하위 컴포넌트에 컨텍스트로 전달
  const [info, setInfo] = useState<DataProps | null>(null);

  const getProfileImageUrl = (user: UserDataProps) => {
    const url = new URL("/oapi/user/pi", API_SERVER_ENDPOINT);

    url.searchParams.set("handle", user.handle);
    if (user.profile_picture_update_ts) {
      url.searchParams.set("d", String(user.profile_picture_update_ts));
    }

    return url.toString();
  };

  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    if (!handle) return;
    let active = true;
    setInfo(null);
    setLoadError(false);
    const query = encodeURIComponent(handle);
    // 필모그래피 실패와 무관한 기본 프로필 표시 및 이전 프로필 응답 차단
    const performanceRequest = apiInstance.get(`/oapi/filmo?handle=${query}`, { isMsg: true })
      .then(res => res.data.is_success && res.data.value ? normalizePerformanceArchive(res.data.value) : createEmptyPerformanceArchive())
      .catch(() => createEmptyPerformanceArchive());
    apiInstance.get(`/oapi/user?handle=${query}`, { isMsg: true })
      .then(async res => {
        if (!res.data.is_success || !res.data.value) throw new Error("Profile unavailable");
        if (!active) return;
        const user = res.data.value;
        setInfo({
          user: { ...user, handle, deep_link: user.branch_link },
          link: user.user_link_list || [],
          video: user.user_video_list || [],
          performance: createEmptyPerformanceArchive(),
        });
        const performance = await performanceRequest;
        if (active) setInfo(previous => previous ? { ...previous, performance } : previous);
      })
      .catch(() => { if (active) setLoadError(true); });
    return () => { active = false; };
  }, [handle]);

  useEffect(() => {
    // 특정 도메인 및 경로 감지
    if (window.location.pathname.startsWith("/profile")) {
      document.documentElement.setAttribute("pretendard-style", "true");
    } else {
      document.documentElement.removeAttribute("pretendard-style");
    }
  }, []);

  useEffect(() => {
    const nickname = info?.user.nickname;
    if (!nickname) return;

    const title = `@${nickname} | HereWeAre`;
    const description =
      info.user.description?.replace(/\s+/g, " ").trim() ||
      `Meet ${nickname} on HereWeAre.`;

    updatePageMeta({
      description,
      image: getProfileImageUrl(info.user),
      title,
      url: window.location.href,
    });
  }, [info?.user]);

  if (loadError) return null;

  if (!info?.user.handle) return <ProfileSkeleton />;

  return <ProfileView key={handle} info={info} content={content} requests={requests} portfolioUserIdx={userIdx ?? info.user.idx} />;
};

export default ProfilePage;
